import { isAxiosError } from 'axios';
import type { ReactNode, SVGProps } from 'react';
import { cn } from '@shared/lib';

/** Общие кусочки игрового интерфейса: пиксельная рамка, звёзды, значки ресурсов, разбор ошибок API. */

type FrameTone = 'dialog' | 'card' | 'accent' | 'danger' | 'success';

const FRAME: Record<FrameTone, { outer: string; inner: string }> = {
  dialog: { outer: 'bg-[#f4ecd8]', inner: 'bg-[#161932]' },
  card: { outer: 'bg-[#3a3a46]', inner: 'bg-[#1b1b22]' },
  accent: { outer: 'bg-accent', inner: 'bg-[#1d1611]' },
  danger: { outer: 'bg-[#e6453b]', inner: 'bg-[#1f1213]' },
  success: { outer: 'bg-[#3dd68c]', inner: 'bg-[#101d17]' },
};

export function PixelFrame({
  tone = 'card',
  className,
  innerClassName,
  children,
}: {
  tone?: FrameTone;
  className?: string;
  innerClassName?: string;
  children: ReactNode;
}) {
  const t = FRAME[tone];
  return (
    <div className={cn('lx-px p-[3px]', t.outer, className)}>
      <div className={cn('lx-px h-full', t.inner, innerClassName)}>{children}</div>
    </div>
  );
}

/** Пиксельная иконка 8×8 (crispEdges): рисунок задан строками. */
function PixelGlyph({ rows, className, ...rest }: { rows: string[] } & SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 8 8" shapeRendering="crispEdges" aria-hidden className={cn('h-4 w-4', className)} {...rest}>
      {rows.flatMap((row, y) =>
        [...row].map((ch, x) => (ch === '#' ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} fill="currentColor" /> : null)),
      )}
    </svg>
  );
}

export const PxCoin = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['..####..', '.#....#.', '#..##..#', '#.#....#', '#..##..#', '#....#.#', '.#....#.', '..####..']} />
);
export const PxClock = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['..####..', '.#....#.', '#...#..#', '#...#..#', '#...###.', '#......#', '.#....#.', '..####..']} />
);
export const PxBattery = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['........', '.######.', '.#....##', '.#.##.##', '.#.##.##', '.#....##', '.######.', '........']} />
);
export const PxHeart = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['........', '.##..##.', '########', '########', '.######.', '..####..', '...##...', '........']} />
);
export const PxStar = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['...##...', '...##...', '########', '.######.', '..####..', '.##..##.', '.#....#.', '........']} />
);
export const PxCheck = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['........', '......##', '.....##.', '#...##..', '##.##...', '.###....', '..#.....', '........']} />
);
export const PxHandshake = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['........', '##....##', '###..###', '.######.', '..####..', '..#..#..', '........', '........']} />
);
export const PxSkull = (p: { className?: string }) => (
  <PixelGlyph className={p.className} rows={['.######.', '########', '#..##..#', '#..##..#', '########', '.##..##.', '.#.##.#.', '........']} />
);

export function PixelStars({ value, max = 3, className }: { value: number; max?: number; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1', className)} aria-label={`${value} / ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <PxStar key={i} className={cn('h-6 w-6', i < value ? 'text-[#f2c14e]' : 'text-[#3a3a46]')} />
      ))}
    </span>
  );
}

export interface StoryErrorInfo {
  code: string | null;
  message: string | null;
  status: number | null;
}

/** Ответ сервера не дошёл: нет сети, обрыв, таймаут. Сам ход мог и не начаться — его можно повторить с тем же номером. */
export function isNetworkFailure(err: unknown): boolean {
  return isAxiosError(err) && !err.response && err.code !== 'ERR_CANCELED';
}

export function storyError(err: unknown): StoryErrorInfo {
  if (!isAxiosError(err)) return { code: null, message: null, status: null };
  const body = err.response?.data as { error?: { code?: string; message?: string } } | undefined;
  return { code: body?.error?.code ?? null, message: body?.error?.message ?? null, status: err.response?.status ?? null };
}
